import { ChevronRight, Clock, MessageSquare, User } from "lucide-react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Badge, TruncatedText } from "@/components/ui";
import { ClickToCopyId } from "@/components/common/ClickToCopyId";
import type { SupportTicket } from "@/services/supportTicketService";
import {
  TicketStatusBadge,
  TicketPriorityBadge,
  TicketCategoryBadge,
} from "./TicketBadges";
import { formatRelativeDate, STATUS_CONFIG, TicketStatus } from "./config";

export type TicketListItemData = Pick<
  SupportTicket,
  | "id"
  | "ticket_number"
  | "subject"
  | "status"
  | "priority"
  | "category"
  | "owner_name"
  | "message_count"
  | "created_at"
  | "last_message_at"
>;

interface TicketListItemProps {
  ticket: TicketListItemData;
  to: string;
  showOwner?: boolean;
  showPriority?: boolean;
  highlightAwaitingReply?: boolean;
  className?: string;
}

export function TicketListItem({
  ticket,
  to,
  showOwner = false,
  showPriority = true,
  highlightAwaitingReply = false,
  className,
}: TicketListItemProps) {
  const { t } = useTranslation("support");
  const statusConfig = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.closed;
  const StatusIcon = statusConfig.icon;
  const needsReply =
    highlightAwaitingReply && ticket.status === TicketStatus.WAITING_RESPONSE;

  return (
    <Link
      to={to}
      className={cn(
        "group hover:bg-surface-50 focus-visible:bg-surface-50 dark:hover:bg-surface-100/50 relative flex items-center gap-3 px-4 py-3.5 transition-colors focus:outline-none sm:gap-4 sm:px-5",
        needsReply && "bg-primary-50/40 dark:bg-primary-50/25",
        className,
      )}
    >
      {needsReply ? (
        <span
          className="bg-primary-500 absolute inset-y-3 left-0 w-0.5 rounded-r-full"
          aria-hidden
        />
      ) : null}
      <span
        className={cn(
          "hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-current/15 ring-inset sm:flex",
          statusConfig.bgColor,
          statusConfig.color,
        )}
        aria-hidden
      >
        <StatusIcon className="h-4 w-4" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <TruncatedText
            as="span"
            className="text-surface-900 min-w-0 text-sm font-semibold tracking-tight"
          >
            {ticket.subject}
          </TruncatedText>
          {needsReply && (
            <Badge
              variant="primary"
              size="sm"
              dot
            >
              {t("list.replyNeeded")}
            </Badge>
          )}
        </span>
        <span className="text-surface-500 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
          <ClickToCopyId
            value={ticket.ticket_number}
            className="text-[11px]"
          />
          <TicketStatusBadge
            status={ticket.status}
            size="sm"
          />
          {showPriority && (
            <TicketPriorityBadge
              priority={ticket.priority}
              size="sm"
            />
          )}
          <TicketCategoryBadge
            category={ticket.category}
            size="sm"
          />
          {showOwner && ticket.owner_name && (
            <span className="flex items-center gap-1.5">
              <User
                className="text-surface-400 h-3.5 w-3.5"
                aria-hidden
              />
              {ticket.owner_name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Clock
              className="text-surface-400 h-3.5 w-3.5"
              aria-hidden
            />
            {formatRelativeDate(ticket.created_at)}
          </span>
          {ticket.message_count > 0 && (
            <span className="flex items-center gap-1.5 tabular-nums">
              <MessageSquare
                className="text-surface-400 h-3.5 w-3.5"
                aria-hidden
              />
              {t("list.messageCountPlain", {
                count: ticket.message_count,
              })}
            </span>
          )}
        </span>
      </span>

      <span className="hidden shrink-0 items-center gap-3 sm:flex">
        {ticket.last_message_at ? (
          <span className="text-surface-400 text-xs tabular-nums">
            {t("list.lastActivityShort", {
              when: formatRelativeDate(ticket.last_message_at),
            })}
          </span>
        ) : null}
        <ChevronRight
          className="text-surface-300 group-hover:text-surface-500 h-4 w-4 transition-transform group-hover:translate-x-0.5"
          aria-hidden
        />
      </span>
    </Link>
  );
}
